import {
  priceProvidersRepositoryAbi,
  siloAbi,
  siloLensAbi,
} from "@silo/contract-abi-types";
import {
  priceProvidersRepositoryAddress,
  siloLensAddress,
} from "@silo/contract-addresses";
import {
  Address,
  AmountFromBigInt,
  ChainPublicClientService,
  PercentFromBigInt,
  ProtocolKeyService,
} from "@silo/types";
import { getContract } from "viem";
import { Schema } from "@effect/schema";
import { Effect, pipe } from "effect";
import { ContractsClientError } from "../types";
import { isRevertError } from "../../utils/settle-reverts";

export type GetViemMarketAssetState = ReturnType<
  typeof getViemMarketAssetState
>;
export type GetViemMarketAssetStateSuccess =
  Effect.Effect.Success<GetViemMarketAssetState>;
export type GetViemMarketAssetStateError =
  Effect.Effect.Error<GetViemMarketAssetState>;
export type GetViemMarketAssetStateContext =
  Effect.Effect.Context<GetViemMarketAssetState>;

interface GetViemMarketAssetStateParams {
  marketAddress: Address;
  tokenAddress: Address;
}

export function getViemMarketAssetState({
  marketAddress,
  tokenAddress,
}: GetViemMarketAssetStateParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;

    const priceProvidersRepositoryContract = getContract({
      address: priceProvidersRepositoryAddress[protocolKey],
      abi: priceProvidersRepositoryAbi,
      client: publicClient,
    });

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const siloContract = getContract({
      address: marketAddress,
      abi: siloAbi,
      client: publicClient,
    });

    const viemMarketAssetStateEncoded = yield* Effect.tryPromise({
      try: async () => {
        const [
          priceNative,
          liquidity,
          utilization,
          collateralTotal,
          rawCollateralBaseApr,
          protectedTotal,
          debtTotal,
          debtBaseApr,
        ] = await Promise.all([
          priceProvidersRepositoryContract.read
            .getPrice([tokenAddress])
            .catch((error) => {
              if (isRevertError(error)) {
                return 0n;
              }

              throw error;
            }),
          siloLensContract.read.liquidity([marketAddress, tokenAddress]),
          siloLensContract.read.getUtilization([marketAddress, tokenAddress]),
          siloLensContract.read.totalDepositsWithInterest([
            marketAddress,
            tokenAddress,
          ]),
          siloLensContract.read.depositAPY([marketAddress, tokenAddress]),
          siloContract.read
            .assetStorage([tokenAddress])
            .then((storage) => storage.collateralOnlyDeposits),
          siloLensContract.read.totalBorrowAmountWithInterest([
            marketAddress,
            tokenAddress,
          ]),
          siloLensContract.read.borrowAPY([marketAddress, tokenAddress]),
        ]);

        const encoded: ViemMarketAssetStateEncoded = {
          priceNative,

          liquidity,
          utilization,

          collateralTotal,
          rawCollateralBaseApr,

          protectedTotal,

          debtTotal,
          debtBaseApr,
        };

        return encoded;
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const viemMarketAssetState = yield* pipe(
      viemMarketAssetStateEncoded,
      Schema.decode(ViemMarketAssetState)
    );

    return viemMarketAssetState;
  });
}

export type ViemMarketAssetState = typeof ViemMarketAssetState.Type;
export type ViemMarketAssetStateEncoded = typeof ViemMarketAssetState.Encoded;

export const ViemMarketAssetState = Schema.Struct({
  priceNative: AmountFromBigInt,

  liquidity: AmountFromBigInt,
  utilization: PercentFromBigInt,

  collateralTotal: AmountFromBigInt,
  rawCollateralBaseApr: PercentFromBigInt,

  protectedTotal: AmountFromBigInt,

  debtTotal: AmountFromBigInt,
  debtBaseApr: PercentFromBigInt,
}).annotations({ identifier: "ViemMarketAssetState" });
